import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; 

//Excluded names need to be hardcoded because video stats(watchtime,likes,comments, etc don't contain the # for particpants)
const excludedNames = ['Admin', 'Andrea', 'Christian', 'Juliana', 'TEST', 'Jorge', 'Luz', 'Xiomara'];

class DatabaseUtil {
    

    static async countDocumentsInVideos() {
        try {
            const videosCollection = collection(db, 'Videos');
            const snapshot = await getDocs(videosCollection);
    
            // Filter out videos intro videos from count
            const filteredVideos = snapshot.docs.filter(doc => 
                !/ENG|ESP/i.test(doc.id) && !/ENG|ESP/i.test(doc.data().title)
            );
    
            return filteredVideos.length; // Total number of videos in DB - intro videos
        } catch (error) {
            console.error('Error counting documents in Videos:', error);
            throw error; 
        }
    }

  static async getLikesAndComments() {
    try {
        const videosCollection = collection(db, 'Videos');
        const snapshot = await getDocs(videosCollection);

        let totalLikes = 0;
        let totalComments = 0;

        snapshot.forEach(doc => {
            const data = doc.data();

            // Filter likes
            const validLikes = Array.isArray(data.Likes)
                ? data.Likes.filter(like => like && !excludedNames.some(name => like.includes(name)))
                : [];

            // Filter comments (exclude entries with excluded usernames)
            const validComments = Array.isArray(data.Comments)
                ? data.Comments.filter(comment => {
                    const username = comment?.Username || ''; 
                    return !excludedNames.some(name => username.includes(name));
                  })
                : [];

            totalLikes += validLikes.length;
            totalComments += validComments.length;
        });

        return { totalLikes, totalComments };
    } catch (error) {
        console.error('Error fetching likes and comments:', error);
        throw error;
    }
}




static async getViewsAndWatchtime() {
    try {
        const videosCollection = collection(db, 'Videos');
        const snapshot = await getDocs(videosCollection);

        let totalViews = 0;
        let totalWatchtimePercentage = 0; 
        let watchtimeEntries = 0;

        snapshot.forEach(doc => {
            const data = doc.data();

            if (data.watchTime && typeof data.watchTime === 'object') {
                // Filter watchTime based on excluded names
                const filteredWatchTime = Object.entries(data.watchTime).filter(([key]) => 
                    !excludedNames.some(name => key.includes(name))
                );

                const watchtimeValues = filteredWatchTime.map(([, time]) => Math.min(time, 100));

                totalViews += watchtimeValues.length;
                totalWatchtimePercentage += watchtimeValues.reduce((sum, value) => sum + value, 0);
                watchtimeEntries += watchtimeValues.length;
            }
        });

        const averageWatchtime = watchtimeEntries > 0 ? (totalWatchtimePercentage / watchtimeEntries) : 0;

        return {
            totalViews, 
            averageWatchtime 
        };
    } catch (error) {
        console.error('Error calculating total video views and average watchtime:', error);
        throw error; 
    }
}



static async getVideoDetailsById(videoId) {
    const phrasesList = [
        'I loved watching the video!',
        'The video was super cool!',
        'The story in this video was awesome!',
        'I want to watch it again!',
    ];
    const badPhrasesList = [
        'The video was not fun to watch.',
        'I found the video boring.',
        'The video didn\'t interest me.',
        'I didn\'t like this video.',
    ];

    const phrasesListESP = [
        '¡Me encantó ver el vídeo!',
        '¡El vídeo estuvo genial!',
        '¡El cuento en este video fue increíble!',
        'Quiero verlo otra vez',
    ];
    const badPhrasesListESP = [
        'El vídeo no fue divertido de ver.',
        'El vídeo me pareció aburrido.',
        'El vídeo no me interesó.',
        'No me gustó este vídeo.',
    ];
    
    try {
        const videoDoc = doc(db, 'Videos', videoId);
        const docSnap = await getDoc(videoDoc);

        if (!docSnap.exists()) {
            throw new Error(`Video with ID ${videoId} not found`);
        }

        const data = docSnap.data();

        // Filter watchTime entries
        const filteredWatchTime = data.watchTime && typeof data.watchTime === 'object'
            ? Object.entries(data.watchTime).filter(
                ([key]) => !excludedNames.some(name => key.includes(name))
              )
            : [];

        const watchTimeValues = filteredWatchTime.map(([, time]) => Math.min(time, 100));
        const totalViews = watchTimeValues.length;
        const averageWatchtime = totalViews > 0
            ? watchTimeValues.reduce((sum, time) => sum + time, 0) / totalViews
            : 0;
        const fullWatchCount = watchTimeValues.filter(time => time >= 90).length;
        const fullWatchRate = totalViews > 0 ? (fullWatchCount / totalViews) * 100 : 0;

        // Filter Likes
        const validLikes = data.Likes && Array.isArray(data.Likes)
            ? data.Likes.filter(like => !excludedNames.some(name => like.includes(name)))
            : [];
        const totalLikes = validLikes.length;

        let totalGoodComments = 0;
        let totalBadComments = 0;
        const commentDetails = [];

        // Process Comments
        if (data.Comments && Array.isArray(data.Comments)) {
            for (const commentObj of data.Comments) {
                const username = commentObj?.Username || ''; // Handle undefined Username
        
                // Skip comments where the Username contains any excluded name
                if (excludedNames.some(name => username.includes(name))) continue;
        
                const text = commentObj.Comment || '';
        
                if (phrasesList.includes(text) || phrasesListESP.includes(text)) {
                    totalGoodComments++;
                } else {
                    totalBadComments++;
                }
        
                commentDetails.push({
                    text,
                    username
                });
            }
        }

        return {
            id: videoId,
            title: data.Title || 'No Title',
            thumbnail: data.Thumbnail,
            totalViews,
            averageWatchtime, 
            fullWatchRate,    
            totalLikes,
            totalComments: commentDetails.length,
            totalGoodComments,
            totalBadComments,
            commentDetails,
            category: data.Category || 'X',
            language: data.Language || 'Spanglish',
            role: data.Role || 'X',
            duration: data.Duration || '0:00',
            url: data.url
        };
    } catch (error) {
        console.error(`Error retrieving video details for ID ${videoId}:`, error);
        throw error;
    }
}


static async getAllVideosDetails() {
    try {
        const videosCollection = collection(db, 'Videos');
        const snapshot = await getDocs(videosCollection);

        const filteredDocs = snapshot.docs.filter(doc => 
            !excludedNames.some(name => doc.id.includes(name) || doc.data().Title?.includes(name))
        );

        const videoDetailsPromises = filteredDocs.map(doc => 
            this.getVideoDetailsById(doc.id)
        );

        return Promise.all(videoDetailsPromises);
    } catch (error) {
        console.error('Error retrieving all videos details:', error);
        throw error;
    }
}


static async getTopVideosByViews() {
    try {
        const videosCollection = collection(db, 'Videos');
        const snapshot = await getDocs(videosCollection);

        let videoViewData = [];

        snapshot.forEach((doc) => {
            const data = doc.data();

            if (data.watchTime && typeof data.watchTime === 'object') {
                // Filter out keys containing excluded names
                const filteredWatchTimeKeys = Object.keys(data.watchTime).filter(
                    key => !excludedNames.some(name => key.includes(name))
                );

                const totalViews = filteredWatchTimeKeys.length;

                videoViewData.push({
                    title: `Video${data.ID}` || 'Untitled',
                    totalViews
                });
            } else {
                videoViewData.push({
                    title: `Video${data.ID}` || 'Untitled',
                    totalViews: 0
                });
            }
        });

        // Sort videos by views in descending order
        return videoViewData.sort((a, b) => b.totalViews - a.totalViews);
    } catch (error) {
        console.error('Error fetching top videos by views:', error);
        throw error;
    }
}

static async getTopVideosByLikes() {
    try {
        const videosCollection = collection(db, 'Videos');
        const snapshot = await getDocs(videosCollection);

        let videoLikesData = [];

        snapshot.forEach((doc) => {
            const data = doc.data();

            // Filter Likes
            const validLikes = data.Likes && Array.isArray(data.Likes)
                ? data.Likes.filter(like => !excludedNames.some(name => like.includes(name)))
                : [];

            videoLikesData.push({
                title: `Video${data.ID}` || 'Untitled',
                totalLikes: validLikes.length
            });
        });

        // Sort videos by likes in descending order
        return videoLikesData.sort((a, b) => b.totalLikes - a.totalLikes);
    } catch (error) {
        console.error('Error fetching top videos by likes:', error);
        throw error;
    }
}


static async getUserDetailsById(userName) {
    try {
        const userDoc = doc(db, 'Users', userName);
        const docSnap = await getDoc(userDoc);

        if (!docSnap.exists()) {
            throw new Error(`User with ID ${userName} not found`);
        }

        const data = docSnap.data();

        let totalViews = 0;
        let totalLikes = 0;
        let totalComments = 0;
        let totalWatchtime = 0;
        let totalEntries = 0;
        let fullWatchCount = 0; 

        //Get subaccount info
        const processSubAccount = (account) => {
            if (account && typeof account === 'object') {
                const watchTimeValues = Object.values(account.watchTime || {});
                totalViews += watchTimeValues.length;
                totalWatchtime += watchTimeValues.reduce((sum, time) => sum + Math.min(time, 100), 0);
                totalEntries += watchTimeValues.length;

                fullWatchCount += watchTimeValues.filter(time => time >= 90).length; 

                totalLikes += Array.isArray(account.Liked_Videos) ? account.Liked_Videos.length : 0;
                totalComments += Array.isArray(account.Comments) ? account.Comments.length : 0;
            }
        };

        processSubAccount(data); // Main account
        ['Caregiver', 'Teacher', 'Classmate'].forEach(sub => processSubAccount(data[sub]));

        const averageWatchtime = totalEntries > 0 ? totalWatchtime / totalEntries : 0;
        const fullWatchRate = totalViews > 0 ? (fullWatchCount / totalViews) * 100 : 0; 

        // Dynamically require the avatar icon
        let avatarIconPath;
        try {
            avatarIconPath = require(`../icons/AvatarIcon ${data.Avatar_Icon || '404'}.png`);
        } catch (e) {
            console.warn(`Avatar Icon not found for user ${userName}, using default.`);
            avatarIconPath = require('../icons/AvatarIcon 404.png'); 
        }

        return {
            userName,
            avatarIcon: avatarIconPath,
            totalViews,
            averageWatchtime: averageWatchtime.toFixed(2),
            fullWatchRate: fullWatchRate.toFixed(2),
            totalLikes,
            totalComments,
        };
    } catch (error) {
        console.error(`Error retrieving user details for ID ${userName}:`, error);
        throw error;
    }
}

static async getAllUsersDetails() {
    try {
        const usersCollection = collection(db, 'Users');
        const snapshot = await getDocs(usersCollection);

        const userDetailsPromises = snapshot.docs.map(doc => 
            this.getUserDetailsById(doc.id)
        );

        return Promise.all(userDetailsPromises);
    } catch (error) {
        console.error('Error retrieving all users details:', error);
        throw error;
    }
}

static async getMainAccountDetails(userName) {
    try {
        const userDoc = doc(db, 'Users', userName);
        const docSnap = await getDoc(userDoc);

        if (!docSnap.exists()) {
            throw new Error(`User with ID ${userName} not found`);
        }

        const data = docSnap.data();

        const watchTimeValues = Object.entries(data.watchTime || {});
        const totalViews = watchTimeValues.length;
        const totalLikes = Array.isArray(data.Liked_Videos) ? data.Liked_Videos.length : 0;
        const totalComments = Array.isArray(data.Comments) ? data.Comments.length : 0;

        const averageWatchtime = totalViews > 0 
            ? (watchTimeValues.reduce((sum, [, time]) => sum + Math.min(time, 100), 0) / totalViews) 
            : 0;

        const fullWatchCount = watchTimeValues.filter(([, time]) => time >= 90).length;
        const fullWatchRate = totalViews > 0 ? (fullWatchCount / totalViews) * 100 : 0;

        // Get top videos by watch time
        const topVideos = watchTimeValues
            .sort(([, timeA], [, timeB]) => timeB - timeA)
            .map(([videoId, time]) => ({ videoId, time }));

        return {
            userName,
            totalViews,
            averageWatchtime: averageWatchtime.toFixed(2),
            fullWatchRate: fullWatchRate.toFixed(2),
            totalLikes,
            totalComments,
            topVideos,
        };
    } catch (error) {
        console.error(`Error retrieving main account details for ID ${userName}:`, error);
        throw error;
    }
}

static async getSubAccountDetails(userName, role) {
    try {
        const userDoc = doc(db, 'Users', userName);
        const docSnap = await getDoc(userDoc);

        if (!docSnap.exists()) {
            throw new Error(`User with ID ${userName} not found`);
        }

        const subAccount = docSnap.data()[role];
        if (!subAccount) {
            throw new Error(`Subaccount ${role} not found for user ${userName}`);
        }

        const watchTimeValues = Object.entries(subAccount.watchTime || {});
        const totalViews = watchTimeValues.length;
        const totalLikes = Array.isArray(subAccount.Liked_Videos) ? subAccount.Liked_Videos.length : 0;
        const totalComments = Array.isArray(subAccount.Comments) ? subAccount.Comments.length : 0;
        
        const averageWatchtime = totalViews > 0 
            ? (watchTimeValues.reduce((sum, [, time]) => sum + Math.min(time, 100), 0) / totalViews) 
            : 0;

        const fullWatchCount = watchTimeValues.filter(([, time]) => time >= 90).length;
        const fullWatchRate = totalViews > 0 ? (fullWatchCount / totalViews) * 100 : 0;

        // Get top videos by watch time
        const topVideos = watchTimeValues
            .sort(([, timeA], [, timeB]) => timeB - timeA)
            .map(([videoId, time]) => ({ videoId, time }));

        return {
            role,
            totalViews,
            totalLikes,
            totalComments,
            averageWatchtime: averageWatchtime.toFixed(2),
            fullWatchRate: fullWatchRate.toFixed(2),
            topVideos,
        };
    } catch (error) {
        console.error(`Error retrieving ${role} details for user ${userName}:`, error);
        throw error;
    }
}


static async getSurveyQuestions(role, language = 'en') {
    try {
        // Map roles to respective JSON files
        const surveyFiles = {
            Student: language === 'es' ? 'ESP Temperature Student Survey' : 'Temperature Student Survey',
            Caregiver: language === 'es' ? 'ESP Temperature Caregiver Survey' : 'Temperature Caregiver Survey',
            Teacher: language === 'es' ? 'ESP Temperature Teacher Survey' : 'Temperature Teacher Survey',
            Classmate: language === 'es' ? 'ESP Temperature Classmate Survey' : 'Temperature Classmate Survey'
        };


        const surveyData = await import(`../json/${surveyFiles[role]}.json`);
        return surveyData.default.Survey || []; 
    } catch (error) {
        console.error('Error loading survey questions:', error);
        throw error;
    }
}

static async getSurveyResponses(role) {
    try {
        const usersCollection = collection(db, 'Users');
        const snapshot = await getDocs(usersCollection);
        const responses = [];
        const filteredUsers = snapshot.docs.filter(doc => doc.id.includes('#'));
        filteredUsers.forEach(doc => {
            const userData = doc.data();
            console.log(`Processing user ${doc.id} for role ${role}`);
            
            let surveyData;
            if (role === 'Student') {
                surveyData = userData.Survey;
            } else {
                surveyData = userData[role]?.Survey;
            }

            if (surveyData) {
                console.log('Found survey data:', surveyData);
                // Convert array-like object to array and add to responses
                Object.keys(surveyData).forEach(index => {
                    responses.push({
                        questionIndex: parseInt(index),
                        answer: surveyData[index]
                    });
                });
            }
        });

        console.log('Collected responses:', responses);
        return responses;
    } catch (error) {
        console.error('Error fetching survey responses:', error);
        throw error;
    }
}

static analyzeSurveyResponses(responses, questions) {
    const analysis = {};
    
    questions.forEach((question, index) => {
        const questionText = question.Question;
        const responseCounts = new Map();
        
        // Get all possible options for this question from the survey
        const possibleOptions = question.Options ? 
            question.Options.map(opt => opt.Option) : [];
        
        // Pre-populate the Map with all possible options (with 0 counts)
        possibleOptions.forEach(option => {
            const combinedAnswer = this.getCombinedAnswer(option);
            if (!responseCounts.has(combinedAnswer)) {
                responseCounts.set(combinedAnswer, 0);
            }
        });
        
        const questionResponses = responses.filter(r => r.questionIndex === index);
        questionResponses.forEach(response => {
            let answer = response.answer;
            let combinedAnswer = this.getCombinedAnswer(answer);
            
            responseCounts.set(combinedAnswer, (responseCounts.get(combinedAnswer) || 0) + 1);
        });

        // Convert Map to array format, ensuring both language versions are shown
        analysis[questionText] = Array.from(responseCounts.entries())
            .map(([key, count]) => {
                const [english, spanish] = key.split('/');
                return [spanish ? `${english}/${spanish}` : key, count];
            })
            .sort((a, b) => b[1] - a[1]);
    });

    return analysis;
}

static getCombinedAnswer(answer) {
    //Equivalent Answers (English/Spanish)
    const equivalentAnswers = {
        //Student responses
        "Making friends": "Hacer amigos",
        "Understanding lessons": "Comprender las lecciones",
        "Doing homework": "Hacer tareas",
        "Every day": "Todos los dias",
        "A few times a week": "Algunas veces a la semana",
        "A few times a month": "Algunas veces al mes",
        "Almost never": "Casi nunca",
        "Comfortable": "Cómodo",
        "Uncomfortable": "Incómodo",
        "Very comfortable": "Muy cómodo",
        "They respect my needs and culture": "Respetan mis necesidades y cultura",
        "They ensure I can participate": "Se aseguran que pueda participar",
        "They include me in activities": "Me incluyen en actividades",
        "Very happy": "Muy feliz",
        "Happy": "Feliz",
        "Incorporate more of my culture and experiences in lessons": "Encontrar cosas que se parezcan a mi en las lecciones y clases",
        "Doing assignments": "Hacer tareas",
        "Participating in activities": "Participar en actividades",
        "Fitting in (being bullied, being teased, name calling, etc.)": "Adaptarse (ser intimidado, objeto de burlas, insultos, etc.)",
        "They adjust lessons for me": "Me adaptan las lecciones",
        "They are understanding and patient": "Son comprensivos y pacientes.",
        "They help me communicate and participate": "Se esfuerzan en comunicarse conmigo",
        "Friends who talk and play with me": "Amigos que hablan y juegan conmigo",
        "Extra help with my learning": "Ayuda extra con mi aprendizaje",
        "English language and expressions": "Lengua inglesa y expresiones",
        "Food and celebrations": "Comidas y celebraciones",
        "Music and dances": "Música y bailes",
        "Teachers who encourage and help me": "Profesores que me animan y ayudan",
        "Activities and clubs I can participate in": "Actividades y clubes en los que puedo participar",
        "Offer more accessible resources and materials": "Ofrecer cosas más accesibles",

        //Caregiver responses
        "A lot": "Mucho",
        "Some": "Algunos",
        "A little": "Un poco",
        "Not at all": "En absoluto",
        "Language barrier": "Barrera del idioma",
        "Cultural differences": "Diferencias culturales",
        "Limited access to resources": "Acceso limitado a los recursos",
        "School attitudes": "Actitudes en la escuela",
        "Information about services and supports": "Información sobre servicios y apoyos",
        "Translated materials": "Materiales en español",
        "Connect with other families": "Conectarse con otras familias",
        "None": "Ninguno",
        "Nothing": "Nada",
        "Unfamiliarity with the education system": "Desconocimiento del sistema educativo",
        "Information about legal rights for parents/caregivers": "Información sobre los derechos legales de los padres/cuidadores",
        "Connecting with other families": "Conectarse con otras familias",
        "Workshops on advocacy": "Talleres de padres",

        //Teacher responses
        "Weekly": "Semnal",
        "Monthly": "Mensual",
        "Quarterly": "Trimestralmente",
        "Very important": "Muy importante",
        "Important": "Importante",
        "Not very important": "Poco importante",
        "Not important at all": "Nada importante",
        "6-10 years": "6-10 años",
        "Yearly": "Anual",
        "Professional Development (trainings, conferences, webinars etc.)": "Desarrollo profesional (capacitaciones, conferencias, seminarios web, etc.)",
        "Peer collaboration and learning": "Colaboración y aprendizaje entre pares",
        "Cultural competency (student background)": "Competencia cultural (antecedentes del estudiante) ",
        "Not important": "No es importante",
        "Partnerships with Latin families": "Asociaciones con familias latinas",
        "Supporting different disabilities": "Apoyo a diferentes discapacidades",
        "Culturally relevant and materials": "Materiales y culturalmente relevantes",
        "Engaging families": "Involucrar a las familias",
        "Culturally relevant lessons, events or activities": "Lecciones, eventos o actividades culturalmente relevantes",
        "Differentiated instruction": "Instrucción diferenciada",
        "Personal family/friends experiences": "Experiencias personales de familiares/amigos",
        "Media (social media, documentaries, etc.) ": "Medios (redes sociales, documentales, etc.)",
        "Travel experiences ": "Experiencias de viaje",
        "1-2 years": "1-2 años",
        "3-5 years": "3-5 años",
        "More than 10 years": "More than 10 años",

        //Classmate responses
        "Very little": "Muy poco",
        "How to include them more in games and activities": "Cómo incluirlos más en juegos y actividades",
        "Spanish language": "Idioma español",
        "I already know some Spanish words": "Ya sé algunas palabras en español.",
        "More information about their disabilities": "Más información sobre sus discapacidades",
        "How to talk and understand each other better": "Cómo hablar y entenderlos mejor",

        //Other responses
        "Beginner": "Principiante",
        "Advanced": "Avanzado",
        "Native or bilingual": "Nativo o bilingüe",
        "Very well": "Muy bien",
        "A little bit": "Un poquito",
        "Little bit": "Un poquito",
        "Not very well": "No muy bien",
        "No experience": "Sin experiencia",
        "Yes": "Si",
        "No": "No",
        "I'm not sure": "No estoy seguro",
        "Neutral (not comfortable or uncomfortable)": "Me da igual",
        "Sometimes": "A veces",
        "Rarely": "Casi nunca",
        "Never": "Nunca",
        "Family": "Familia",
        "Family ": "Familia",
        "Some do, some don't": "Algunos lo hacen, otros no",
        "Does not matter": "No importa",
        "The Spanish language": "El idioma"
    };

    // Check if answer has an equivalent
    for (const [english, spanish] of Object.entries(equivalentAnswers)) {
        if (answer === english) return `${english}/${spanish}`;
        if (answer === spanish) return `${english}/${spanish}`;
    }

    //If no equivalent just return the answer w/o other language
    return answer;
}

static async getTemperatureData(role, language = 'en') {
    try {
        const surveyFiles = {
            Student: language === 'es' ? 'ESP Temperature Student Survey' : 'Temperature Student Survey',
            Caregiver: language === 'es' ? 'ESP Temperature Caregiver Survey' : 'Temperature Caregiver Survey',
            Teacher: language === 'es' ? 'ESP Temperature Teacher Survey' : 'Temperature Teacher Survey',
            Classmate: language === 'es' ? 'ESP Temperature Classmate Survey' : 'Temperature Classmate Survey'
        };

        console.log(`Loading temperature data for role: ${role}, language: ${language}`);
        
        const surveyData = await import(`../json/${surveyFiles[role]}.json`);
        console.log('Loaded survey data:', surveyData);
        
        const temperatureData = surveyData.default.Temperature || {};
        console.log('Temperature data:', temperatureData);
        
        return temperatureData;
    } catch (error) {
        console.error('Error loading temperature data:', error);
        throw error;
    }
}

static async getCategoryStats(role) {
    try {
        const videos = await this.getAllVideosDetails();
        const roleVideos = videos.filter(video => video.role === role);
        
        const categoryViews = {};
        const categoryWatchtime = {};
        const categoryCount = {};

        for (let i = 1; i <= 15; i++) 
        {
            categoryViews[i] = 0;
            categoryWatchtime[i] = 0;
            categoryCount[i] = 0;
        }

        // Process each video
        roleVideos.forEach(video => {
            const category = video.category;
            if (category) 
                { 
                //Tester accounts already fitlered
                categoryViews[category] += video.totalViews;
                categoryWatchtime[category] += parseFloat(video.averageWatchtime);
                categoryCount[category]++;
            }
        });

        Object.keys(categoryWatchtime).forEach(category => {
            if (categoryCount[category] > 0) 
                {
                categoryWatchtime[category] = categoryWatchtime[category] / categoryCount[category];
            }
        });

        return {
            views: categoryViews,
            watchtime: categoryWatchtime
        };
    } catch (error) {
        console.error('Error getting category statistics:', error);
        throw error;
    }
}
static async getUserCategoryStats(userName, role) {
    try {
        const userDoc = doc(db, 'Users', userName);
        const docSnap = await getDoc(userDoc);

        if (!docSnap.exists()) {
            throw new Error(`User with ID ${userName} not found`);
        }

        const categoryViews = {};
        const categoryWatchtime = {};
        const categoryCount = {};

        for (let i = 1; i <= 15; i++) {
            categoryViews[i] = 0;
            categoryWatchtime[i] = 0;
            categoryCount[i] = 0;
        }

        const accountData = role === 'Student' ? 
            docSnap.data() : 
            docSnap.data()[role];

        if (!accountData?.watchTime) {
            return { views: categoryViews, watchtime: categoryWatchtime };
        }

        const videosCollection = collection(db, 'Videos');
        const videosSnapshot = await getDocs(videosCollection);
        const videoCategories = {};
        
        videosSnapshot.forEach(doc => {
            videoCategories[doc.id] = doc.data().Category;
        });

        Object.entries(accountData.watchTime).forEach(([videoId, time]) => {
            const category = videoCategories[videoId];
            if (category) {
                categoryViews[category]++;
                categoryWatchtime[category] += Math.min(time, 100);
                categoryCount[category]++;
            }
        });

        Object.keys(categoryWatchtime).forEach(category => {
            if (categoryCount[category] > 0) {
                categoryWatchtime[category] = categoryWatchtime[category] / categoryCount[category];
            }
        });

        return {
            views: categoryViews,
            watchtime: categoryWatchtime
        };
    } catch (error) {
        console.error('Error getting user category statistics:', error);
        throw error;
    }
}

}

export default DatabaseUtil;
